import { motion } from 'framer-motion';
import { FaLinkedin, FaGithub, FaEnvelope } from 'react-icons/fa';
import type { IconType } from 'react-icons';
import { content } from '@/content/content';
import type { SocialKey } from '@/content/content';

const channelIcons: Record<SocialKey, IconType> = {
  email: FaEnvelope,
  linkedin: FaLinkedin,
  github: FaGithub,
};

const rowClass =
  'group grid grid-cols-[auto_1fr_auto] items-center gap-4 border-t border-[color:var(--hairline)] py-5 transition-colors';

const valueClass =
  'block truncate text-[color:var(--fg)] transition-colors group-hover:text-primary-ink dark:group-hover:text-primary';

const markClass =
  'font-mono text-[color:var(--fg-muted)] transition-all group-hover:translate-x-0.5 group-hover:text-primary-ink dark:group-hover:text-primary';

/** Ledger of direct channels: mono type label over value, `↗` affordance per row. */
const ContactChannels = (): JSX.Element => {
  const { contact, social } = content;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.45 }}
      className="border-b border-[color:var(--hairline)]"
    >
      {contact.channels.map(({ type, text }) => {
        const Icon = channelIcons[type];
        const { url, label } = social[type];
        const external = type !== 'email';

        return (
          <a
            key={type}
            href={url}
            aria-label={label}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className={rowClass}
          >
            <Icon className="text-primary-ink dark:text-primary" size="1.4rem" aria-hidden="true" />
            <span className="min-w-0">
              <span className="eyebrow block">{type}</span>
              <span className={valueClass}>{text}</span>
            </span>
            <span aria-hidden="true" className={markClass}>
              ↗
            </span>
          </a>
        );
      })}
    </motion.div>
  );
};

export default ContactChannels;
